import { useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, BookOpen, Landmark, FileText, Quote, HelpCircle, Briefcase, LogOut, Menu, X, ExternalLink,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTitle } from "../../hooks/useTitle";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/courses", label: "Courses", icon: BookOpen },
  { to: "/admin/services", label: "Services", icon: Landmark },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/applications", label: "Applications", icon: Briefcase },
];

export default function AdminLayout() {
  useTitle("Admin Panel");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-[#1F2937]">
        <div className="w-9 h-9 bg-[#0A192F] rounded-lg flex items-center justify-center border border-[#C59B27]/70 shrink-0">
          <span className="text-[#C59B27] font-display font-extrabold text-xs">SN</span>
        </div>
        <div>
          <p className="font-display font-extrabold text-sm text-[#F9FAFB]">SterlingNest</p>
          <p className="text-[9px] font-mono-sn text-[#9CA3AF] tracking-widest">ADMIN PANEL</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1" data-testid="admin-nav">
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${l.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                isActive ? "bg-[#111827] text-[#10B981]" : "text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#111827]/60"
              }`
            }
          >
            <l.icon size={17} /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-[#1F2937] space-y-1">
        <Link to="/" className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold text-[#9CA3AF] hover:text-[#F9FAFB] transition-colors" data-testid="admin-view-site">
          <ExternalLink size={17} /> View Website
        </Link>
        <button
          onClick={doLogout}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <LogOut size={17} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0F19] text-[#F9FAFB]" data-testid="admin-panel">
      <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#080C14] border-b border-[#1F2937]">
        <span className="font-display font-extrabold text-sm">SterlingNest Admin</span>
        <button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu-toggle" className="p-2 text-[#9CA3AF]">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-[#080C14] border-b border-[#1F2937]">{sidebar}</div>
      )}
      <div className="flex">
        <aside className="hidden lg:block w-60 shrink-0 bg-[#080C14] border-r border-[#1F2937] min-h-screen sticky top-0 h-screen overflow-y-auto">
          {sidebar}
        </aside>
        <main className="flex-1 min-w-0 p-5 sm:p-8">
          <div className="mb-6 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">Signed in as <span className="text-[#9CA3AF] font-semibold">{user?.email}</span></p>
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
